import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type IncentiveZoneRecordView, type RiskAreaGeometryView } from '@vello/api-client';
import { Badge, Button, CheckRow, EmptyState, Field, FormRow, ListTable, Measure, Notice, PageHeader, Panel, Row, SectionTitle, SegmentedControl, Select, Stack, Text, TextArea, TextInput, type Column, AUTO_REFRESH_MS, useAutoRefresh, Drawer, DrawerHeader, DrawerBody, DrawerFooter, KeyValueList } from '@vello/web-theme';
import { DrawerStack } from '../components/drawer-stack';
import { ScopeMarketNotice } from '../components/scope-market';
import { IdRef } from '../components/code-ref';
import { PersonName } from '../components/person-name';
import {
  maySelfApprove,
  EMPTY_ZONE_DRAFT,
  adminScreenMarket,
  adminScreenMarketChoices,
  formatMarketInstant,
  groupIncentiveZones,
  incentiveZoneAuthority,
  marketTimeZone,
  parseLatLng,
  roundPoint,
  utcToZonedLocal,
  zoneDecideBlock,
  zoneDraftFromDefinition,
  zoneDraftToProposal,
  zoneInForce,
  zoneStatusTone,
  type IncentiveZoneRow,
  type ZoneDraft,
} from '../../src/index';
import { SelfApprovalNote } from '../components/self-approval-note';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * Operações › Zonas de incentivo (W10-MARKET-SIGNALS, D488, ADR-0101; doc 17 §3.2): an operator proposes a zone — the points of a
 * polygon or a centre and a radius, a name, a priority and the window in the market's zone — and a DIFFERENT operator approves
 * it; until then it counts nothing. Editing or switching a zone off is a new version under the same rule. The approved zones are
 * the unit the market signals (requests, accepts, ETA, drivers per 5-minute window) are counted in and the incentive controller
 * reads. A market with no zone still has the implicit whole-market zone. Layout and type come from the web-theme vocabulary (no inline style). Neutral: no country,
 * zone name or language is named here.
 */

export function IncentiveZonesScreen(): ReactElement {
  const { runtime, scope, markets, grants, me } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const authority = incentiveZoneAuthority(grants);

  const choices = useMemo(() => adminScreenMarketChoices(scope, markets), [scope, markets]);
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const market = adminScreenMarket(scope, choices, picked);
  const showPicker = scope.current.city === undefined && choices.length > 1;
  const timeZone = marketTimeZone(market, markets);

  const [records, setRecords] = useState<readonly IncentiveZoneRecordView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [draft, setDraft] = useState<ZoneDraft>(EMPTY_ZONE_DRAFT);
  const [typed, setTyped] = useState('');
  const [formIssue, setFormIssue] = useState<string | undefined>(undefined);
  const [rejecting, setRejecting] = useState<IncentiveZoneRecordView | undefined>(undefined);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // The record drawer (the Directory pattern): the zone stays selected while the drawer closes.
  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (reasonCode: string | undefined, fallback: string, status?: number): string => {
      const text = refusalOf('refusal.admin.incentive_zone', { code: fallback, reasonCode, status });
      return text ?? t('lmap.err.generic', { code: fail({ code: fallback, reasonCode }) });
    },
    [t, fail, refusalOf],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setRecords(undefined);
      setError(undefined);
      return;
    }
    const res = await runtime.ops.incentiveZones(market);
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setRecords(res.value.zones);
      setError(undefined);
    } else {
      // A refused read keeps the list unknown: the screen says it could not load, never "no zones".
      setError(refuse(res.error.reasonCode, res.error.code, res.error.status));
    }
  }, [runtime, market, refuse]);

  useEffect(() => {
    setRecords(undefined);
    setDraft(EMPTY_ZONE_DRAFT);
    setRejecting(undefined);
    setDrawerOpen(false);
    void load();
  }, [load]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== undefined });

  const rows = useMemo(() => groupIncentiveZones(records ?? [], new Date().toISOString()), [records]);
  const nowIso = new Date().toISOString();
  const canDraw = authority.mayManage && market !== undefined;

  const addTyped = (): void => {
    const p = parseLatLng(typed);
    if (p === undefined) {
      setFormIssue('izone.issue.COORDINATE_INVALID');
      return;
    }
    const r = roundPoint(p);
    setDraft((d) => (d.shape === 'polygon' ? { ...d, ring: [...d.ring, r] } : { ...d, center: r }));
    setFormIssue(undefined);
    setTyped('');
  };

  const propose = async (): Promise<void> => {
    if (market === undefined) return;
    const built = zoneDraftToProposal(draft, timeZone);
    if (!built.ok) {
      setFormIssue(`izone.issue.${built.issue}`);
      return;
    }
    setBusy(true);
    setNotice(undefined);
    setFormIssue(undefined);
    const res = await runtime.ops.proposeIncentiveZone(market, built.input);
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t('izone.done.proposed') });
      setDraft(EMPTY_ZONE_DRAFT);
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const decide = async (record: IncentiveZoneRecordView, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    if (market === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.decideIncentiveZone(market, record.definition.zoneId, record.definition.version, { decision, ...(decision === 'REJECT' ? { reason } : {}) });
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: t(decision === 'APPROVE' ? 'izone.done.approved' : 'izone.done.rejected') });
      setRejecting(undefined);
      setReason('');
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const edit = (row: IncentiveZoneRow): void => {
    const base = (row.pending ?? row.approved ?? row.latest).definition;
    setDraft(zoneDraftFromDefinition(base, (utc) => (timeZone !== undefined ? utcToZonedLocal(utc, timeZone) : undefined)));
    setFormIssue(undefined);
    setNotice(undefined);
    setDrawerOpen(false);
  };

  const columns: readonly Column<IncentiveZoneRow>[] = [
    {
      key: 'what',
      header: t('izone.col.zone'),
      width: 'minmax(220px, 1.6fr)',
      render: (r) => (
        <Stack gap={1}>
          <Text as="strong">{r.latest.definition.name}</Text>
          <Text role="caption" tone="secondary">
            {v('geo.shape_kind', (r.latest.definition.geometry as { kind: string }).kind === 'circle' ? 'circle' : 'polygon')} · {t('izone.priority', { value: r.latest.definition.priority })} · {t('izone.versions', { value: r.versions })}
          </Text>
        </Stack>
      ),
    },
    {
      key: 'state',
      header: t('izone.col.state'),
      width: 'minmax(190px, 1.2fr)',
      render: (r) => (
        <Row gap={2} wrap>
          {r.approved !== undefined ? (
            <Badge tone={zoneInForce(r, nowIso) ? 'success' : 'neutral'}>{t(zoneInForce(r, nowIso) ? 'izone.state.inForce' : r.approved.definition.active ? 'izone.state.outOfWindow' : 'izone.state.off')}</Badge>
          ) : (
            <Badge tone="neutral">{t('izone.state.notApproved')}</Badge>
          )}
          {r.pending !== undefined && <Badge tone={zoneStatusTone('PROPOSED')}>{t('izone.state.pending', { value: r.pending.definition.version })}</Badge>}
          {r.pending === undefined && r.latest.status === 'REJECTED' && <Badge tone={zoneStatusTone('REJECTED')}>{t('izone.state.rejected', { value: r.latest.definition.version })}</Badge>}
        </Row>
      ),
    },
    {
      key: 'window',
      header: t('izone.col.window'),
      width: 'minmax(210px, 1.3fr)',
      render: (r) => {
        const d = (r.approved ?? r.latest).definition;
        return (
          <Text role="caption" tone="secondary" className="tnum">
            {formatMarketInstant(d.effectiveFrom, timeZone, lang)} → {d.effectiveTo !== undefined ? formatMarketInstant(d.effectiveTo, timeZone, lang) : t('izone.openEnded')}
          </Text>
        );
      },
    },
    {
      key: 'trail',
      header: t('izone.col.trail'),
      width: 'minmax(190px, 1.2fr)',
      render: (r) => {
        const rec = r.pending ?? r.approved ?? r.latest;
        return (
          <Text role="caption" tone="secondary">
            <PersonName id={rec.proposedBy} />
            {rec.decidedBy !== undefined && (
              <>
                {' → '}
                <PersonName id={rec.decidedBy} />
              </>
            )}
          </Text>
        );
      },
    },
  ];

  const rowActions = (r: IncentiveZoneRow): ReactElement => {
    const pending = r.pending;
    const block = pending !== undefined ? zoneDecideBlock(pending, me?.id, authority.mayManage, selfApproval) : undefined;
    return (
      <Row gap={2} wrap align="center">
        {authority.mayManage && pending === undefined && <Button variant="secondary" disabled={busy} onClick={() => edit(r)}>{t('izone.action.edit')}</Button>}
        {pending !== undefined && block === undefined && (
          <>
            <Button variant="secondary" disabled={busy} onClick={() => void decide(pending, 'APPROVE')}>{t('izone.action.approve')}</Button>
            <Button variant="ghost" disabled={busy} onClick={() => startReject(r.zoneId, pending)}>{t('izone.action.reject')}</Button>
          </>
        )}
        {pending !== undefined && block === 'OWN_PROPOSAL' && <Text role="caption" tone="secondary">{t('izone.waitOther')}</Text>}
      </Row>
    );
  };

  const openRecord = (zoneId: string): void => {
    setOpenId(zoneId);
    setDrawerOpen(true);
    setRejecting(undefined);
    setReason('');
  };
  const closeDrawer = (): void => {
    setDrawerOpen(false);
    setRejecting(undefined);
    setReason('');
  };
  const startReject = (zoneId: string, pending: IncentiveZoneRecordView): void => {
    setOpenId(zoneId);
    setDrawerOpen(true);
    setRejecting(pending);
    setReason('');
    setNotice(undefined);
  };
  const drawerRow = rows.find((r) => r.zoneId === openId);

  const set = <K extends keyof ZoneDraft>(key: K, value: ZoneDraft[K]): void => setDraft((d) => ({ ...d, [key]: value }));
  const issueText = formIssue !== undefined ? t(formIssue) : undefined;

  return (
    <Stack gap={5}>
      <PageHeader title={t('izone.title')} subtitle={t('izone.subtitle')} />

      {!authority.mayManage && <Notice tone="neutral">{t('izone.readOnly')}</Notice>}

      {showPicker && (
        <Panel>
          <Measure size="sm">
            <Field label={t('w6.market.field')} htmlFor="izone-market" hint={t('w6.market.hint')}>
              <Select id="izone-market" value={market ?? ''} onChange={(e) => setPicked(e.target.value === '' ? undefined : e.target.value)}>
                <option value="">{t('w6.market.choose')}</option>
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </Field>
          </Measure>
        </Panel>
      )}

      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          <Text role="caption" tone="secondary" as="p">{t('izone.marketWide')}</Text>
          <ListTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.zoneId}
            onRowClick={(r) => openRecord(r.zoneId)}
            selectedKey={drawerOpen ? openId : undefined}
            actions={rowActions}
            loading={records === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('izone.empty.failed') : undefined}
            onRetry={() => void load()}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('izone.empty.title')} detail={t('izone.empty.detail')} />}
            minWidth={1100}
          />

          {canDraw && (
            <Panel>
              <SectionTitle>{t(draft.zoneId !== undefined ? 'izone.form.titleEdit' : 'izone.form.title')}</SectionTitle>
              <Text role="caption" tone="secondary" as="p">{t('izone.form.fourEyes')}</Text>
              <Measure size="lg" gap={4}>
                <Field label={t('izone.form.name')} htmlFor="izone-name" hint={t('izone.form.nameHint')}>
                  <TextInput id="izone-name" value={draft.name} maxLength={80} onChange={(e) => set('name', e.target.value)} />
                </Field>

                <SegmentedControl<'polygon' | 'circle'>
                  ariaLabel={t('izone.form.shape')}
                  value={draft.shape}
                  onChange={(v) => set('shape', v)}
                  options={[{ id: 'polygon', label: v('geo.shape_kind', 'polygon') }, { id: 'circle', label: v('geo.shape_kind', 'circle') }]}
                />

                <Stack gap={3}>
                  <FormRow
                    columns="minmax(0, 360px)"
                    action={
                      <>
                        <Button variant="secondary" onClick={addTyped}>{t(draft.shape === 'polygon' ? 'izone.form.addPointAction' : 'izone.form.setCenter')}</Button>
                        {draft.shape === 'polygon' && draft.ring.length > 0 && <Button variant="ghost" onClick={() => set('ring', draft.ring.slice(0, -1))}>{t('izone.form.undoPoint')}</Button>}
                      </>
                    }
                  >
                    <Field label={t(draft.shape === 'polygon' ? 'izone.form.addPoint' : 'izone.form.center')} htmlFor="izone-point" hint={t('izone.form.pointHint')}>
                      <TextInput id="izone-point" className="tnum" value={typed} placeholder="-0.000000, 0.000000" onChange={(e) => setTyped(e.target.value)} />
                    </Field>
                  </FormRow>
                  {draft.shape === 'polygon' ? (
                    <Text role="caption" tone="secondary" as="p" className="tnum">{t('izone.form.points', { value: draft.ring.length })}{draft.ring.length > 0 ? ` — ${draft.ring.map((p) => `${p.latitude}, ${p.longitude}`).join(' · ')}` : ''}</Text>
                  ) : (
                    <>
                      {draft.center !== undefined && <Text role="caption" tone="secondary" as="p" className="tnum">{draft.center.latitude}, {draft.center.longitude}</Text>}
                      <Field label={t('izone.form.radius')} htmlFor="izone-radius">
                        <TextInput id="izone-radius" className="tnum" inputMode="numeric" value={draft.radiusMeters} onChange={(e) => set('radiusMeters', e.target.value.replace(/[^\d]/g, ''))} />
                      </Field>
                    </>
                  )}
                </Stack>

                <Field label={t('izone.form.priority')} htmlFor="izone-priority" hint={t('izone.form.priorityHint')}>
                  <TextInput id="izone-priority" className="tnum" inputMode="numeric" value={draft.priority} onChange={(e) => set('priority', e.target.value.replace(/[^\d]/g, ''))} />
                </Field>

                <Row gap={4} wrap>
                  <Field label={t('izone.form.from')} htmlFor="izone-from" hint={t('izone.form.zoneHint', { value: timeZone ?? 'UTC' })}>
                    <TextInput id="izone-from" className="tnum" type="datetime-local" value={draft.from} onChange={(e) => set('from', e.target.value)} />
                  </Field>
                  <Field label={t('izone.form.to')} htmlFor="izone-to" hint={t('izone.form.toHint')}>
                    <TextInput id="izone-to" className="tnum" type="datetime-local" value={draft.to} onChange={(e) => set('to', e.target.value)} />
                  </Field>
                </Row>

                <CheckRow id="izone-active" checked={draft.active} onChange={(on) => set('active', on)}>{t('izone.form.active')}</CheckRow>

                {issueText !== undefined && <Notice tone="danger">{issueText}</Notice>}
                <Row gap={3}>
                  <Button variant="primary" disabled={busy} onClick={() => void propose()}>{t('izone.action.propose')}</Button>
                  <Button variant="ghost" disabled={busy} onClick={() => { setDraft(EMPTY_ZONE_DRAFT); setFormIssue(undefined); }}>{t('izone.action.clear')}</Button>
                </Row>
              </Measure>
            </Panel>
          )}
        </>
      )}
      <Drawer open={drawerOpen} onClose={closeDrawer} labelledBy="izone-drawer-title">
        {drawerRow !== undefined && (() => {
          const rec = drawerRow.pending ?? drawerRow.approved ?? drawerRow.latest;
          const def = rec.definition;
          const geo = def.geometry as RiskAreaGeometryView;
          const pending = drawerRow.pending;
          const block = pending !== undefined ? zoneDecideBlock(pending, me?.id, authority.mayManage, selfApproval) : undefined;
          const askingReject = rejecting !== undefined && pending !== undefined && rejecting.definition.version === pending.definition.version;
          return (
            <>
              <DrawerHeader
                titleId="izone-drawer-title"
                title={def.name}
                actions={
                  <Row gap={2} wrap>
                    {drawerRow.approved !== undefined ? (
                      <Badge tone={zoneInForce(drawerRow, nowIso) ? 'success' : 'neutral'}>{t(zoneInForce(drawerRow, nowIso) ? 'izone.state.inForce' : drawerRow.approved.definition.active ? 'izone.state.outOfWindow' : 'izone.state.off')}</Badge>
                    ) : (
                      <Badge tone="neutral">{t('izone.state.notApproved')}</Badge>
                    )}
                    {pending !== undefined && <Badge tone={zoneStatusTone('PROPOSED')}>{t('izone.state.pending', { value: pending.definition.version })}</Badge>}
                    {pending === undefined && drawerRow.latest.status === 'REJECTED' && <Badge tone={zoneStatusTone('REJECTED')}>{t('izone.state.rejected', { value: drawerRow.latest.definition.version })}</Badge>}
                  </Row>
                }
                onClose={closeDrawer}
                closeLabel={t('ui.close')}
              />
              <DrawerBody>
                <DrawerStack>
                  <KeyValueList
                    items={[
                      { label: t('izone.drawer.shape'), value: v('geo.shape_kind', geo.kind === 'circle' ? 'circle' : 'polygon') },
                      ...(geo.kind === 'circle'
                        ? [{ label: t('izone.form.radius'), value: String(geo.radiusMeters), mono: true }]
                        : geo.kind === 'polygon'
                          ? [{ label: t('izone.form.points'), value: String(geo.ring.length), mono: true }]
                          : []),
                      { label: t('izone.form.priority'), value: String(def.priority), mono: true },
                      { label: t('izone.col.window'), value: `${formatMarketInstant(def.effectiveFrom, timeZone, lang)} → ${def.effectiveTo !== undefined ? formatMarketInstant(def.effectiveTo, timeZone, lang) : t('izone.openEnded')}`, mono: true },
                      { label: t('izone.drawer.version'), value: `${def.version} / ${drawerRow.versions}`, mono: true },
                      { label: t('izone.drawer.proposedBy'), value: <PersonName id={rec.proposedBy} /> },
                      { label: t('izone.drawer.proposedAt'), value: formatMarketInstant(rec.proposedAt, timeZone, lang), mono: true },
                      { label: t('izone.drawer.fourEyes'), value: t(`izone.drawer.fourEyes.${rec.status}`) },
                      ...(rec.decidedBy !== undefined ? [{ label: t('izone.drawer.decidedBy'), value: <><PersonName id={rec.decidedBy} /><SelfApprovalNote record={rec} /></> }] : []),
                      ...(rec.decidedAt !== undefined ? [{ label: t('izone.drawer.decidedAt'), value: formatMarketInstant(rec.decidedAt, timeZone, lang), mono: true }] : []),
                      ...(rec.decisionReason !== undefined ? [{ label: t('izone.drawer.reason'), value: rec.decisionReason }] : []),
                      { label: t('izone.drawer.id'), value: <IdRef value={drawerRow.zoneId} copy full />, mono: true },
                    ]}
                  />
                  {askingReject && (
                    <Field label={t('izone.reject.reason')} htmlFor="izone-reason" hint={t('izone.reject.hint')}>
                      <TextArea id="izone-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                  )}
                  {pending !== undefined && block === 'OWN_PROPOSAL' && <Notice tone="neutral">{t('izone.waitOther')}</Notice>}
                </DrawerStack>
              </DrawerBody>
              {askingReject && pending !== undefined ? (
                <DrawerFooter>
                  <Button variant="secondary" disabled={busy || reason.trim() === ''} onClick={() => void decide(pending, 'REJECT')}>{t('izone.action.reject')}</Button>
                  <Button variant="ghost" disabled={busy} onClick={() => { setRejecting(undefined); setReason(''); }}>{t('ui.cancel')}</Button>
                </DrawerFooter>
              ) : (
                ((authority.mayManage && pending === undefined) || (pending !== undefined && block === undefined)) && (
                  <DrawerFooter>
                    {authority.mayManage && pending === undefined && <Button variant="secondary" disabled={busy} onClick={() => edit(drawerRow)}>{t('izone.action.edit')}</Button>}
                    {pending !== undefined && block === undefined && (
                      <>
                        <Button variant="secondary" disabled={busy} onClick={() => void decide(pending, 'APPROVE')}>{t('izone.action.approve')}</Button>
                        <Button variant="ghost" disabled={busy} onClick={() => startReject(drawerRow.zoneId, pending)}>{t('izone.action.reject')}</Button>
                      </>
                    )}
                  </DrawerFooter>
                )
              )}
            </>
          );
        })()}
      </Drawer>
    </Stack>
  );
}
